import { useTranslation } from 'react-i18next'
import {
  AccessDeniedPage as BaseAccessDeniedPage,
  AccessDeniedStatus,
  getDefaultStatusConfig
} from '@airclou/ui'
import { useAuth } from '../../contexts/AuthContext'
import { SUPPORT_EMAIL } from '../../config'

interface AccessDeniedPageProps {
  status?: AccessDeniedStatus
  message?: string
}

export function AccessDeniedPage({ 
  status = 'no_access', 
  message 
}: AccessDeniedPageProps) {
  const { t } = useTranslation('dashboard')
  const { user, logout } = useAuth()

  // Dashboard-specific status configuration
  const getDashboardStatusConfig = (status: AccessDeniedStatus, message?: string) => {
    const baseConfig = getDefaultStatusConfig(status, message)

    // Customize titles for dashboard context
    switch (status) {
      case 'inactive':
        return {
          ...baseConfig,
          title: t('accessDenied.inactive'),
          description: message || t('accessDenied.inactiveDesc')
        }
      case 'suspended':
        return {
          ...baseConfig,
          title: t('accessDenied.suspended'),
          description: message || t('accessDenied.suspendedDesc')
        }
      case 'pending_verification':
        return {
          ...baseConfig,
          title: t('accessDenied.pendingVerification'),
          description: message || t('accessDenied.pendingVerificationDesc')
        }
      default:
        return {
          ...baseConfig,
          title: t('accessDenied.noAccess'),
          description: message || t('accessDenied.noAccessDesc')
        }
    }
  }

  const handleContactAdmin = () => {
    const config = getDashboardStatusConfig(status, message)
    const subject = encodeURIComponent(`Dashboard Access Issue - ${config.title}`)
    const body = encodeURIComponent(`
Hello,

I am experiencing an issue with my dashboard access:

- Status: ${status}
- User Email: ${user?.email || 'Not available'}
- User ID: ${user?.id || 'Not available'}
- Issue: ${config.description}

Please assist me in resolving this issue.

Thank you,
${user?.name || 'User'}
    `.trim())
    
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`
  }

  const handleRetry = () => {
    window.location.reload()
  }

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
      window.location.href = '/login'
    }
  }

  return (
    <BaseAccessDeniedPage
      status={status}
      message={message}
      user={user || undefined}
      onContactAdmin={handleContactAdmin}
      onRetry={handleRetry}
      onLogout={handleLogout}
      getStatusConfig={getDashboardStatusConfig}
    />
  )
}
